import { Accordion, AccordionDetails, AccordionSummary, Stack, Box, Grid, IconButton } from '@mui/material';
import Typography from '@mui/material/Typography';
import { ExpandMore, DeleteOutlined } from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations.tsx';
import AddToCartButton from '@/components/common/add-to-cart-button.tsx';
import useCreateEventOrderMutation from '@/state/mutations/orders/use-create-order.ts';
import { type ProductKey, PRODUCT_KEYS, PRODUCT_SCOPE_TYPES } from '@/types/dtos/orders/response/product.dto.ts';
import { getProductCatalogEntry } from '@/constants/product-catalog.ts';
import { useEffect, useMemo, useState } from 'react';
import { ORDER_STATUS, type OrderDto, type OrderStatus } from '@/types/dtos/orders/response/order.dto.ts';
import useUpdateOrderMutation from '@/state/mutations/orders/use-update-order.ts';
import { useNotifications } from '@/hooks/use-notifications';
import { useProductsStore } from '@/state/stores/use-products-store.ts';
import type { EventDto } from '@/types/dtos/events/response/event.dto.ts';
import EventCatalogProductQuotas from '@/components/events/event-catalog/event-catalog-product-quotas.tsx';
import { useQueryClient } from '@tanstack/react-query';
import { GET_EVENT_ORDERS_QUERY_KEY } from '@/state/queries/orders/use-get-event-orders.ts';
import { GET_PRODUCTS_QUERY_KEY } from '@/state/queries/orders/use-get-products';
import { GET_EVENT_QUERY_KEY } from '@/state/queries/events/use-get-event';
import { getOriginEventId } from '@/helpers/event.helper';
import {
  getOrderLineItemProductId,
  mapOrderLineItemsToUpdatePayload,
  upsertOrderLineItemInPayload,
} from '@/helpers/order-line-item.helper';
import type { OrderLineItemDto } from '@/types/dtos/orders/response/order-line-item.dto';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { EVENT_CATALOG_ORDERS_FEATURE } from '@/constants/feature-flags';

const PAYMENT_PENDING_REFETCH_INTERVAL = 3000;

function orderLineItemMatchesProductScope(
  lineItem: OrderLineItemDto,
  productId: string,
  contributorId?: string,
): boolean {
  if (getOrderLineItemProductId(lineItem.product) !== productId) {
    return false;
  }
  const lineContributorId = lineItem.contributorId ?? undefined;
  if (contributorId !== undefined) {
    return lineContributorId === contributorId;
  }
  return lineContributorId === undefined;
}

export default function EventCatalogProductDetails({
  productKey,
  event,
  orders,
  expanded,
  readonly,
  onCheckoutRequested,
  onRemoveLineItem,
  scopeLabel,
  contributorId,
  quantity = 1,
  showPrice,
  titleOverride,
  subtitleOverride,
  detailsOverride,
}: {
  productKey: ProductKey;
  orders?: OrderDto[];
  event?: EventDto;
  expanded?: boolean;
  readonly?: boolean;
  onCheckoutRequested?: () => void;
  onRemoveLineItem?: () => void;
  /** When set (e.g. contributor-scoped order line), shown instead of the catalog subtitle. */
  scopeLabel?: string;
  /** When set, add-to-cart creates a contributor-scoped order line. */
  contributorId?: string;
  quantity?: number;
  showPrice?: boolean;
  /** When set, replaces the catalog title (e.g. entitlement modal scoped vs pool offers). */
  titleOverride?: string;
  /** When set, replaces the catalog subtitle and `scopeLabel`. */
  subtitleOverride?: string;
  /** When set, replaces catalog details (` - ` separated bullets). */
  detailsOverride?: string;
}) {
  const t = useTranslations();
  const notification = useNotifications();
  const { hasAccess: hasCatalogOrderAccess, isLoading: catalogOrderAccessLoading } =
    useFeatureAccess(EVENT_CATALOG_ORDERS_FEATURE);
  const { mutateAsync: createEventOrder, isPending: createPending } = useCreateEventOrderMutation();
  const { mutateAsync: updateOrder, isPending: updatePending } = useUpdateOrderMutation();
  const products = useProductsStore((s) => s.products);
  const catalogCopy = useMemo(() => {
    const entry = getProductCatalogEntry(productKey) ?? getProductCatalogEntry(PRODUCT_KEYS.CONTRIBUTOR);
    return entry!;
  }, [productKey]);
  const [previousOrderStatus, setPreviousOrderStatus] = useState<OrderStatus | undefined>();
  const queryClient = useQueryClient();

  const productData = useMemo(
    () =>
      products?.find(
        (product) =>
          product.key === productKey &&
          String(product.scopeType).toLowerCase() === PRODUCT_SCOPE_TYPES.EVENT.toLowerCase(),
      ),
    [products, productKey],
  );

  // Compute latestOrder from orders
  const latestOrder = useMemo(
    () => orders?.find((order) => order.status === ORDER_STATUS.DRAFT || order.status === ORDER_STATUS.PAYMENT_PENDING),
    [orders],
  );

  const isLineItemInDraftOrder = useMemo(
    () =>
      productData?.id != null &&
      (latestOrder?.lineItems.some((lineItem) =>
        orderLineItemMatchesProductScope(lineItem, productData.id, contributorId),
      ) ??
        false),
    [latestOrder, productData, contributorId],
  );

  const shoppingCartCountItems = useMemo(
    () =>
      productData?.id == null
        ? 0
        : (latestOrder?.lineItems
            .filter((lineItem) => orderLineItemMatchesProductScope(lineItem, productData.id, contributorId))
            .reduce((sum, lineItem) => sum + lineItem.quantity, 0) ?? 0),
    [latestOrder, productData, contributorId],
  );

  // Track order status changes during render
  const currentOrder = useMemo(
    () =>
      orders?.find((order) =>
        order.lineItems.some((lineItem) => getOrderLineItemProductId(lineItem.product) === productData?.id),
      ),
    [orders, productData?.id],
  );

  const [prevOrdersForStatus, setPrevOrdersForStatus] = useState(orders);
  if (prevOrdersForStatus !== orders && currentOrder && currentOrder.status !== previousOrderStatus) {
    setPrevOrdersForStatus(orders);
    setPreviousOrderStatus(currentOrder.status);
  }

  // Periodically invalidate orders query when there are payment pending orders
  useEffect(() => {
    const hasPaymentPendingOrders = orders?.some((order) => order.status === ORDER_STATUS.PAYMENT_PENDING);
    const originEventId = getOriginEventId(event);

    if (!hasPaymentPendingOrders || !originEventId) {
      return;
    }

    const interval = setInterval(() => {
      queryClient.invalidateQueries({
        queryKey: [GET_EVENT_ORDERS_QUERY_KEY, originEventId],
      });
      queryClient.invalidateQueries({
        queryKey: [GET_EVENT_QUERY_KEY, originEventId],
      });
      queryClient.invalidateQueries({
        queryKey: [GET_PRODUCTS_QUERY_KEY],
      });
    }, PAYMENT_PENDING_REFETCH_INTERVAL);

    return () => clearInterval(interval);
  }, [orders, event, queryClient]);

  const handleAddOrOpenCheckout = async () => {
    const originEventId = getOriginEventId(event);
    if (!hasCatalogOrderAccess || !productData || !originEventId || readonly) {
      return;
    }

    if (isLineItemInDraftOrder) {
      onCheckoutRequested?.();
      return;
    }

    try {
      const linePayload = {
        productId: productData.id,
        quantity,
        ...(contributorId ? { contributorId } : {}),
        ...(catalogCopy?.autoAssignToOrderScope && !contributorId ? { autoAssignToOrderScope: true } : {}),
      };
      const useUpsert = contributorId != null || quantity !== 1;

      if (latestOrder?.status === ORDER_STATUS.DRAFT) {
        const lineItems = useUpsert
          ? upsertOrderLineItemInPayload(latestOrder.lineItems, linePayload)
          : [...mapOrderLineItemsToUpdatePayload(latestOrder.lineItems), linePayload];
        await updateOrder({
          orderId: latestOrder.id,
          lineItems,
        });
      } else {
        await createEventOrder({
          eventId: originEventId,
          lineItems: [linePayload],
        });
      }

      onCheckoutRequested?.();
    } catch {
      notification.notifyError(t('event.features.notification.addFeatureError'));
    }
  };

  const isPaymentPending = latestOrder?.status === ORDER_STATUS.PAYMENT_PENDING;

  const checkoutButton = () => {
    if (readonly || catalogOrderAccessLoading || !hasCatalogOrderAccess) {
      return null;
    }

    return (
      <AddToCartButton
        countItems={isLineItemInDraftOrder && !isPaymentPending ? shoppingCartCountItems : 0}
        isLoading={createPending || updatePending || isPaymentPending}
        onClick={handleAddOrOpenCheckout}
        testId={`add-${productKey}-catalog-line-button`}
        eventId={getOriginEventId(event)}
      />
    );
  };

  return (
    <Grid container alignItems="center">
      <Grid
        size={{ xs: 9, sm: 10 }}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ '&:first-of-type': { mb: 1 } }}
      >
        <Accordion
          defaultExpanded={expanded}
          disabled={readonly}
          elevation={0}
          sx={{
            'width': '100%',
            'backgroundColor': 'transparent',
            'boxShadow': 'none',
            '&:before': { display: 'none' },
            '&.Mui-disabled': { backgroundColor: 'transparent' },
            '& .MuiButtonBase-root.Mui-disabled': { opacity: 1 },
          }}
        >
          <AccordionSummary
            expandIcon={!readonly && <ExpandMore />}
            sx={{
              'alignItems': 'center',
              'span.MuiAccordionSummary-content': { m: 0 },
              'backgroundColor': 'transparent',
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }} gap={1}>
              <EventCatalogProductQuotas event={event} product={productData} />
              <Stack sx={{ width: '100%' }}>
                <Typography variant="h6">{titleOverride ?? t(catalogCopy.titleKey)}</Typography>
                {subtitleOverride ? (
                  <Typography variant="body2">{subtitleOverride}</Typography>
                ) : scopeLabel ? (
                  <Typography variant="body2" fontWeight={500}>
                    {scopeLabel}
                  </Typography>
                ) : (
                  <Typography variant="body2">{t(catalogCopy.subtitleKey)}</Typography>
                )}
              </Stack>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0, ul: { m: 0, paddingInlineStart: '1rem' } }}>
            <ul>
              {(detailsOverride ?? t(catalogCopy.detailsKey)).split(' - ').map((item) => (
                <Typography key={item} variant="body2" component="li">
                  {item}
                </Typography>
              ))}
            </ul>
          </AccordionDetails>
        </Accordion>
      </Grid>

      <Grid size={{ xs: 3, sm: 2 }} display="flex" alignItems="center" justifyContent="end">
        <Stack alignItems="flex-end" spacing={0.5}>
          {showPrice && productData ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
              €{productData.pricePerUnit.toFixed(2)}
            </Typography>
          ) : null}
          {checkoutButton()}
        </Stack>
        {onRemoveLineItem && (
          <IconButton aria-label="delete" onClick={onRemoveLineItem}>
            <DeleteOutlined />
          </IconButton>
        )}
      </Grid>
    </Grid>
  );
}
